/**
 * HTTP-обёртки для Hermes-эндпоинтов. docs/modules/hermes-estimate.md §6.
 */
import { apiRequest } from '../../shared/api/client.ts';
import type {
  HermesEstimateConfig,
  UpsertHermesConfigRequest,
  TestHermesKeyResponse,
  HermesEstimateResponse,
  HermesItemResult,
  HermesEstimateLogEntry,
  HermesHistoryEntry,
  HermesDialog,
  HermesEstimationMode,
  HermesScenario,
  ApplyHermesScenarioResponse,
  BulkHermesEstimateRequest,
  HermesEmbedSessionResponse,
  HermesEmbedPreset,
  JobResponse,
  HermesStand,
  HermesStandOption,
  CreateHermesStandRequest,
  UpdateHermesStandRequest,
  HermesConfigSource,
  CopyHermesConfigRequest,
  CopyHermesConfigResponse,
} from '@lens/shared';

// ─── Стенды Hermes ─────────────────────────────────────────────────────────
// Список стендов правит админ экземпляра (/admin/settings); в настройках
// пространства стенд только выбирают.

export async function listHermesStands(): Promise<HermesStand[]> {
  return apiRequest('/api/admin/hermes-stands');
}

export async function createHermesStand(body: CreateHermesStandRequest): Promise<HermesStand> {
  return apiRequest('/api/admin/hermes-stands', { method: 'POST', body });
}

export async function updateHermesStand(
  standId: string,
  body: UpdateHermesStandRequest,
): Promise<HermesStand> {
  return apiRequest(`/api/admin/hermes-stands/${standId}`, { method: 'PATCH', body });
}

export async function deleteHermesStand(standId: string): Promise<void> {
  await apiRequest(`/api/admin/hermes-stands/${standId}`, { method: 'DELETE' });
}

export async function testHermesStand(standId: string): Promise<TestHermesKeyResponse> {
  return apiRequest(`/api/admin/hermes-stands/${standId}/test`, { method: 'POST', body: {} });
}

/** Стенды, доступные для выбора в пространстве (без ключей). */
export async function listSpaceHermesStands(spaceId: string): Promise<HermesStandOption[]> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-stands`);
}

// ─── Перенос настроек оценки из другого пространства ───────────────────────

export async function listHermesConfigSources(): Promise<HermesConfigSource[]> {
  return apiRequest('/api/hermes-configs/sources');
}

export async function copyHermesConfigFrom(
  spaceId: string,
  itemTypeId: string,
  body: CopyHermesConfigRequest,
): Promise<CopyHermesConfigResponse> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-configs/${itemTypeId}/copy-from`, {
    method: 'POST',
    body,
  });
}

export async function listHermesConfigs(spaceId: string): Promise<HermesEstimateConfig[]> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-configs`);
}

export async function getHermesConfig(
  spaceId: string,
  itemTypeId: string,
): Promise<HermesEstimateConfig> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-configs/${itemTypeId}`);
}

export async function upsertHermesConfig(
  spaceId: string,
  itemTypeId: string,
  body: UpsertHermesConfigRequest,
): Promise<HermesEstimateConfig> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-configs/${itemTypeId}`, { method: 'PUT', body });
}

export async function deleteHermesConfig(spaceId: string, itemTypeId: string): Promise<void> {
  await apiRequest(`/api/spaces/${spaceId}/hermes-configs/${itemTypeId}`, { method: 'DELETE' });
}

export async function testHermesKey(
  spaceId: string,
  itemTypeId: string,
  body: { standId?: string; apiKey?: string; baseUrl?: string },
): Promise<TestHermesKeyResponse> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-configs/${itemTypeId}/test-key`, {
    method: 'POST',
    body,
  });
}

export async function getHermesLog(
  spaceId: string,
  itemTypeId: string,
): Promise<HermesEstimateLogEntry[]> {
  return apiRequest(`/api/spaces/${spaceId}/hermes-configs/${itemTypeId}/log`);
}

/** Запуск оценки одной записи → jobId. Режим выбирается при запуске. */
export async function estimateItem(
  spaceId: string,
  itemId: string,
  estimationMode?: HermesEstimationMode,
): Promise<HermesEstimateResponse> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-estimate`, {
    method: 'POST',
    body: estimationMode ? { estimationMode } : {},
  });
}

/** Массовая оценка → jobId. */
export async function bulkEstimate(
  spaceId: string,
  body: BulkHermesEstimateRequest,
): Promise<HermesEstimateResponse> {
  return apiRequest(`/api/spaces/${spaceId}/items/bulk-hermes-estimate`, { method: 'POST', body });
}

/** Записать выбранный сценарий последней оценки в поля Lens. */
export async function applyHermesScenario(
  spaceId: string,
  itemId: string,
  scenario: HermesScenario,
): Promise<ApplyHermesScenarioResponse> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-apply-scenario`, {
    method: 'POST',
    body: { scenario },
  });
}

/** Последний результат оценки записи (для панели). */
export async function getItemHermesResult(
  spaceId: string,
  itemId: string,
): Promise<HermesItemResult> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-result`);
}

/** История завершённых оценок записи. */
export async function getItemHermesHistory(
  spaceId: string,
  itemId: string,
): Promise<HermesHistoryEntry[]> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-history`);
}

/** Разговор оценки в Гермесе: вопросы суб-агентов и ответы человека. */
export async function getHermesEstimateDialog(
  spaceId: string,
  itemId: string,
  logId: string,
): Promise<HermesDialog> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-estimates/${logId}/dialog`);
}

/** Открыть интерактивную (embed) сессию оценки → токен + origin для iframe. */
export async function createEmbedSession(
  spaceId: string,
  itemId: string,
  estimationMode?: HermesEstimationMode,
): Promise<HermesEmbedSessionResponse> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-embed-session`, {
    method: 'POST',
    body: estimationMode ? { estimationMode } : {},
  });
}

/** Пресеты оценки для селектора на экране старта. Пустой список — прячем селектор. */
export async function fetchHermesPresets(
  spaceId: string,
  itemId: string,
): Promise<HermesEmbedPreset[]> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-presets`);
}

/** Финализировать embed-оценку по task_id из hermes:done → актуальный результат. */
export async function finalizeEmbedEstimate(
  spaceId: string,
  itemId: string,
  taskId: string,
): Promise<HermesItemResult> {
  return apiRequest(`/api/spaces/${spaceId}/items/${itemId}/hermes-embed-finalize`, {
    method: 'POST',
    body: { taskId },
  });
}

/** Поллинг job — переиспользуем общий /api/jobs/:id. */
export async function getJob(jobId: string): Promise<JobResponse> {
  return apiRequest(`/api/jobs/${jobId}`);
}

/** Отмена job (групповая оценка). */
export async function cancelJob(jobId: string): Promise<void> {
  await apiRequest(`/api/jobs/${jobId}/cancel`, { method: 'POST' });
}
